import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import { Html, Plane, useGLTF } from "@react-three/drei";
import ReactPlayer from "react-player";

const BASE_SCREEN_SCALE = 0.06;

const ScreensPreview = ({ socle, layer, layerName }) => {
  const [video, setVideo] = useState(null);
  const [isProvided, setIsProvided] = useState(null);
  const { nodes, materials } = useGLTF("/3dAssets/screenWithSocle.glb");
  const videoPlayerRef = useRef();
  const notProvidedVideoRef = useRef();
  const screenRef = useRef();
  const notProvidedScreenRef = useRef();
  const isScreenHeightSetRef = useRef(false);
  const videoCountRef = useRef(0);
  const initialScreenSize = useRef(null);

  const handleVideoSize = () => {
    if (!initialScreenSize.current) {
      let sizeVector = new THREE.Vector3();
      let box3 = new THREE.Box3().setFromObject(
        notProvidedScreenRef.current,
        true
      );
      box3.getSize(sizeVector);
      initialScreenSize.current = sizeVector;
    }
    const isLandscape =
      notProvidedVideoRef.current.videoHeight <
      notProvidedVideoRef.current.videoWidth;
    const ratio = !isLandscape
      ? notProvidedVideoRef.current.videoHeight /
        notProvidedVideoRef.current.videoWidth
      : notProvidedVideoRef.current.videoWidth /
        notProvidedVideoRef.current.videoHeight;

    notProvidedScreenRef.current.scale.set(
      initialScreenSize.current.x,
      initialScreenSize.current.y * (isLandscape ? 1 / ratio : ratio),
      initialScreenSize.current.z
    );

    notProvidedScreenRef.current.position.set(
      notProvidedScreenRef.current.position.x,
      !isLandscape ? initialScreenSize.current.y - (ratio - 1) : 0, ///////// to check
      notProvidedScreenRef.current.position.z
    );
  };

  const getVideoUrl = () => {
    if (!layer.links[videoCountRef.current].hasOwnProperty("name")) {
      setIsProvided(true);
      return layer.links[videoCountRef.current].link;
    } else {
      setIsProvided(false);
      const vid = document.createElement("video");
      notProvidedVideoRef.current = vid;
      vid.src = layer.links[videoCountRef.current].link;
      vid.addEventListener("ended", handleVideoEnd, false);
      vid.addEventListener("loadedmetadata", handleVideoSize, false);
      vid.crossOrigin = "Anonymous";
      vid.loop = layer.loop;
      vid.muted = layer.mute;
      vid.play();
      return vid;
    }
  };

  useEffect(() => {
    setVideo(getVideoUrl());

    return () => {
      if (!isProvided) {
        notProvidedVideoRef.current?.pause();
        notProvidedVideoRef.current?.removeEventListener(
          "ended",
          handleVideoEnd
        );
        notProvidedVideoRef.current?.removeEventListener(
          "loadedmetadata",
          handleVideoSize
        );
      }
    };
  }, []);

  const handleVideoEnd = (e) => {
    initialScreenSize.current = null;
    if (!e) {
      e = window.event;
    }
    if (videoCountRef.current === layer.links.length - 1)
      videoCountRef.current = 0;
    else videoCountRef.current += 1;
    setVideo(getVideoUrl());
  };

  const getScreenPosition = useCallback(
    (startTile, endTile, layer, scale = false, isEastOrWest = false) => {
      const { row, column } = startTile;
      if (
        !endTile ||
        (startTile.row === endTile.row && startTile.column === endTile.column)
      )
        return scale ? 1 : 0;
      else {
        let x = row > endTile.row ? endTile.row : row;
        let start = row < endTile.row ? endTile.row : row;

        let y = column < endTile.column ? column : endTile.column;
        let end = column > endTile.column ? column : endTile.column;
        const offset = startTile.column > endTile.column ? -1 : 1;
        const rowOffset = startTile.row > endTile.row ? -1 : 1;
        if (scale) {
          if (layer === "north" || layer === "south") return end - y + 1;
          else return start - x + 1;
        }
        if (layer === "north" || layer === "south") {
          return (myScale * offset) / 2 - 0.5;
        } else if ((layer === "east" || layer === "west") && isEastOrWest) {
          return (myScale * offset * rowOffset) / 2 - 0.5;
        } else return 0;
      }
    },
    []
  );

  const myScale = useMemo(() => {
    return getScreenPosition(
      layer.startTile || { row: layer.row, column: layer.column },
      layer.endTile,
      layerName,
      true
    );
  }, [layer, layerName]);

  const myPosition = useMemo(() => {
    return [
      layerName === "east"
        ? 0.4
        : layerName === "west"
        ? -0.4
        : getScreenPosition(
            layer.startTile || { row: layer.row, column: layer.column },
            layer.endTile,
            layerName
          ),
      1.6,
      layerName === "north"
        ? -0.4
        : layerName === "south"
        ? 0.4
        : getScreenPosition(
            layer.startTile || { row: layer.row, column: layer.column },
            layer.endTile,
            layerName,
            false,
            true
          ),
    ];
  }, [layer, layerName, getScreenPosition]);

  const resizeScreen = () => {
    if (!isScreenHeightSetRef.current) {
      let sizeVector = new THREE.Vector3();
      let box3 = new THREE.Box3().setFromObject(videoPlayerRef.current, true);
      box3.getSize(sizeVector);
      videoPlayerRef.current.position.set(
        videoPlayerRef.current.position.x,
        sizeVector.y / 2 + 1.1, // match unity calculus
        videoPlayerRef.current.position.z
      );
      isScreenHeightSetRef.current = true;
    }
  };

  const myRotation = useMemo(() => {
    return layerName === "south"
      ? Math.PI
      : layerName === "east"
      ? -Math.PI / 2
      : layerName === "west"
      ? Math.PI / 2
      : 0;
  }, [layerName]);

  const soclePosition = useMemo(() => {
    return layerName === "north" || layerName === "south"
      ? [myPosition[0], 0, 0]
      : [0, 0, myPosition[2]];
  }, [layerName, myPosition]);

  if (video && isProvided !== null) {
    return (
      <group>
        <group dispose={null} position={myPosition} rotation-y={myRotation}>
          <mesh
            geometry={nodes.screen.geometry}
            material={materials.iPadContourMaterial}
            ref={notProvidedScreenRef}
            scale={[myScale, myScale / 1.7, myScale]}
            visible={!isProvided}
          >
            {!isProvided && (
              <Plane position={[0, 0, 0.015]} scale={0.95}>
                <meshStandardMaterial side={THREE.DoubleSide}>
                  <videoTexture attach="map" args={[video]} />
                  <videoTexture attach="emissiveMap" args={[video]} />
                </meshStandardMaterial>
              </Plane>
            )}
          </mesh>
        </group>
        <group
          dispose={null}
          position={myPosition}
          rotation-y={myRotation}
          scale={myScale}
          ref={videoPlayerRef}
          visible={isProvided}
        >
          <mesh material={materials.iPadContourMaterial} ref={screenRef} />
          <mesh geometry={nodes.screen.geometry} scale={BASE_SCREEN_SCALE}>
            <Html style={{ userSelect: "none" }} occlude="blending" transform>
              <ReactPlayer
                url={video}
                playing={layer.autoplay}
                controls
                muted={layer.mute}
                volume={0.5} // mandatory to set volume in order to make muted props works
                loop={layer.loop}
                onEnded={handleVideoEnd} // called once the playlist is over, so set one source and change after
                onReady={() => {
                  resizeScreen();
                }}
              />
            </Html>
          </mesh>
        </group>
        {socle && (
          <group position={soclePosition} rotation-y={myRotation}>
            <mesh
              geometry={nodes.socle.geometry}
              material={materials.iPadSocleMaterial}
            />
            <mesh
              geometry={nodes.woodSocle.geometry}
              material={materials.woodSocleMaterial}
            />
            <mesh
              geometry={nodes.Cube008.geometry}
              material={materials.iPadMaterial}
            />
            <mesh
              geometry={nodes.Cube008_1.geometry}
              material={materials.iPadScreen}
            />
            <mesh
              geometry={nodes.Cube008_2.geometry}
              material={materials.iPadContourMaterial}
            />
          </group>
        )}
      </group>
    );
  } else return null;
};

export default ScreensPreview;

// useGLTF.preload("/screenWithIpad.glb");
